import { useState } from 'react';
import { createUserWithEmailAndPassword, updateProfile, signInWithPopup, linkWithCredential, GoogleAuthProvider } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { auth, db, googleProvider } from '../firebase/config';
import { Link, useNavigate } from 'react-router-dom';
import Modal from '../components/Modal';

function LockIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function AlertIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}

export default function Signup() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Account linking state
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkPassword, setLinkPassword] = useState('');
  const [linkLoading, setLinkLoading] = useState(false);
  const [linkError, setLinkError] = useState(null);
  const [pendingCredential, setPendingCredential] = useState(null);
  const [pendingEmail, setPendingEmail] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );

      await updateProfile(userCredential.user, {
        displayName: formData.name
      });

      await setDoc(doc(db, 'users', userCredential.user.uid), {
        displayName: formData.name,
        email: formData.email,
        role: 'user',
        createdAt: new Date()
      });

      navigate('/');
    } catch (err) {
      if (err.code === 'auth/email-already-in-use') {
        setError('Email is already in use');
      } else if (err.code === 'auth/weak-password') {
        setError('Password is too weak');
      } else {
        setError('Failed to create account');
        console.error(err);
      }
      setLoading(false);
    }
  };

  const handleGoogleSignup = async () => {
    setError(null);
    setLoading(true);

    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;

      await setDoc(doc(db, 'users', user.uid), {
        displayName: user.displayName || user.email?.split('@')[0] || 'User',
        email: user.email,
        role: 'user',
        createdAt: new Date()
      });

      navigate('/');
    } catch (err) {
      if (err.code === 'auth/account-exists-with-different-credential') {
        const email = err.customData?.email;
        const credential = GoogleAuthProvider.credentialFromError(err);
        setPendingCredential(credential);
        setPendingEmail(email);
        setShowLinkModal(true);
      } else if (err.code !== 'auth/popup-closed-by-user') {
        setError(err.message || 'Failed to sign up with Google.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLinkGoogle = async (e) => {
    e.preventDefault();
    setLinkError(null);
    setLinkLoading(true);

    try {
      const userCredential = await import('firebase/auth').then(mod =>
        mod.signInWithEmailAndPassword(auth, pendingEmail, linkPassword)
      );
      await linkWithCredential(userCredential.user, pendingCredential);
      navigate('/');
    } catch (err) {
      if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setLinkError('Incorrect password. Please try again.');
      } else {
        setLinkError('Failed to link accounts. Please try again.');
      }
    } finally {
      setLinkLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <a
          href="https://www.sohera.co.za/"
          className="sohera-back"
          style={{ marginBottom: '1.25rem' }}
        >
          ← Back to SoHera
        </a>

        <div className="auth-logo">
          <img src="/logo-dark.png" alt="SoHera_" className="auth-logo-img" />
          <p className="auth-logo-subtitle">Invoice System · Create your account</p>
        </div>

        {error && (
          <div className="auth-error">
            <AlertIcon />
            <span>{error}</span>
          </div>
        )}

        <form className="auth-form" onSubmit={handleSubmit}>
          <div className="auth-input-group">
            <label htmlFor="name" className="auth-label">Full Name</label>
            <input
              id="name"
              name="name"
              type="text"
              required
              className="auth-input"
              placeholder="Full Name"
              value={formData.name}
              onChange={handleChange}
            />
          </div>

          <div className="auth-input-group">
            <label htmlFor="email-address" className="auth-label">Email address</label>
            <input
              id="email-address"
              name="email"
              type="email"
              autoComplete="email"
              required
              className="auth-input"
              placeholder="Email address"
              value={formData.email}
              onChange={handleChange}
            />
          </div>

          <div className="auth-input-group">
            <label htmlFor="password" className="auth-label">Password</label>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="new-password"
              required
              className="auth-input"
              placeholder="Password"
              value={formData.password}
              onChange={handleChange}
            />
          </div>

          <div className="auth-input-group">
            <label htmlFor="confirm-password" className="auth-label">Confirm Password</label>
            <input
              id="confirm-password"
              name="confirmPassword"
              type="password"
              autoComplete="new-password"
              required
              className="auth-input"
              placeholder="Confirm Password"
              value={formData.confirmPassword}
              onChange={handleChange}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="auth-button"
          >
            {loading ? 'Creating account...' : 'Sign up'}
          </button>

          <div className="auth-divider">
            <span>or</span>
          </div>

          <button
            type="button"
            onClick={handleGoogleSignup}
            disabled={loading}
            className="auth-google-btn"
          >
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {loading ? 'Signing up...' : 'Continue with Google'}
          </button>

          <div className="auth-footer">
            <p>
              Already have an account?{' '}
              <Link to="/login" className="auth-link">
                Sign in
              </Link>
            </p>
          </div>
        </form>
      </div>

      <Modal
        isOpen={showLinkModal}
        onClose={() => {
          setShowLinkModal(false);
          setLinkPassword('');
          setLinkError(null);
        }}
        title="Connect Google Account"
      >
        <p style={{ fontSize: '0.9rem', color: '#4b5563', marginBottom: '0.75rem' }}>
          An account with <strong>{pendingEmail}</strong> already exists.
        </p>
        <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '1rem' }}>
          Enter your password to connect your Google account to it.
        </p>

        {linkError && (
          <div className="auth-error" style={{ marginBottom: '1rem' }}>
            <AlertIcon />
            <span>{linkError}</span>
          </div>
        )}

        <form onSubmit={handleLinkGoogle}>
          <div className="auth-input-group">
            <label htmlFor="link-password" className="auth-label">Password</label>
            <div className="auth-input-wrap">
              <LockIcon />
              <input
                id="link-password"
                type="password"
                className="auth-input"
                placeholder="Enter your existing password"
                value={linkPassword}
                onChange={(e) => {
                  setLinkPassword(e.target.value);
                  if (linkError) setLinkError(null);
                }}
                required
                autoFocus
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={linkLoading}
            className="auth-button"
            style={{ marginTop: '0.5rem' }}
          >
            {linkLoading ? 'Connecting...' : 'Connect & Sign In'}
          </button>
        </form>
      </Modal>
    </div>
  );
}
